<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, nextTick } from 'vue'
import Swiper from 'swiper'
import { FreeMode } from 'swiper/modules'
import { useBreakpoints } from '@/lib/utils'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import Skeleton from '@/components/Skeleton.vue'
import {
	fetchShoppingTips,
	type IShoppingTipsBrandItem,
} from '@/assets/ts/apiManagement'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const { isLarge } = useBreakpoints()

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	description: {
		type: String,
		default: '',
	},
	countryId: {
		type: String,
		required: true,
	},
	buttonText: {
		type: String,
		required: true,
	},
	buttonLink: {
		type: String,
		required: true,
	},
})

const swiperEl = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null
const canGoPrev = ref(false)
const canGoNext = ref(true)

const swiperOptions = {
	modules: [FreeMode],
	slidesPerView: 'auto' as const,
	spaceBetween: 12,
	speed: 500,
	allowTouchMove: true,
	freeMode: {
		enabled: true,
		momentumRatio: 0.5,
		momentumVelocityRatio: 0.5,
		momentumBounce: false,
	},
	on: {
		slideChange: () => {
			updateNavigationState()
		},
		reachBeginning: () => {
			canGoPrev.value = false
		},
		reachEnd: () => {
			canGoNext.value = false
		},
		fromEdge: () => {
			canGoPrev.value = !swiper?.isBeginning
			canGoNext.value = !swiper?.isEnd
		},
	},
	breakpoints: {
		1280: {
			slidesPerView: 4,
			slidesPerGroup: 4,
			spaceBetween: 20,
			allowTouchMove: false,
			freeMode: false,
		},
	},
}

const goPrev = () => {
	if (!swiper || !canGoPrev.value) return
	swiper.slidePrev(500)
}

const goNext = () => {
	if (!swiper || !canGoNext.value) return
	swiper.slideNext(500)
}

const updateNavigationState = () => {
	if (!swiper) return
	canGoPrev.value = !swiper.isBeginning
	canGoNext.value = !swiper.isEnd
}

const initializeSwiper = () => {
	if (!swiperEl.value) return
	swiper = new Swiper(swiperEl.value, swiperOptions)
	updateNavigationState()
}

const allBrands = ref<IShoppingTipsBrandItem[]>([])
const loading = ref(false)

const fetchBrands = async () => {
	loading.value = true

	try {
		const response = await fetchShoppingTips()
		allBrands.value = response.data.content.websites || []
	} catch (e) {
		console.error('Error fetching brands:', e)
	} finally {
		loading.value = false
	}
}

const filteredBrands = computed(() => {
	const MAX_BRANDS = 8

	return allBrands.value
		.filter(brand => brand.country_id === props.countryId)
		.slice(0, MAX_BRANDS)
		.map(brand => ({
			image: brand.logo_url,
			title: brand.description,
			link: brand.link_url,
		}))
})

const handleBrandsCardClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'Brand Card',
	})
}
const handleButtonClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.ShippingGlobal,
		button_name: 'View More - Shipping Tips',
	})
}

onMounted(async () => {
	await fetchBrands()
	await nextTick()
	initializeSwiper()
})

onUnmounted(() => {
	swiper?.destroy()
})
</script>

<template>
	<section class="bg-o-50 py-32px lg:py-72px">
		<div class="bs-container flex flex-col items-center gap-24px lg:gap-32px">
			<!-- Section Header -->
			<div class="flex w-full flex-col items-center gap-8px">
				<h2
					class="text-center text-n-950"
					:class="isLarge ? 'typography-h2-str' : 'typography-h3-str'"
				>
					{{ title }}
				</h2>
				<p
					v-if="description"
					class="text-center text-n-500"
					:class="isLarge ? 'typography-s1-reg' : 'typography-s2-reg'"
				>
					{{ description }}
				</p>
			</div>

			<!-- Brands Carousel Container -->
			<div class="relative w-full">
				<!-- Loading Skeleton -->
				<div
					v-if="loading"
					class="!-mx-[20px] overflow-x-auto !px-[20px] mdp:!-mx-[32px] mdp:!px-[32px] lg:!mx-0 lg:!px-0"
				>
					<div class="flex gap-12px lg:grid lg:grid-cols-4 lg:gap-20px">
						<div
							v-for="i in isLarge ? 4 : 8"
							:key="`skeleton-${i}`"
							class="flex flex-shrink-0 flex-col gap-12px rounded-sm bg-white pb-12px"
							:class="{ '!w-[264px]': !isLarge }"
						>
							<Skeleton class="h-[198px] w-full lg:h-[214.5px]" />
							<div class="flex flex-col gap-8px px-12px">
								<Skeleton class="h-[24px] w-full" />
								<Skeleton class="h-[24px] w-[75%]" />
							</div>
						</div>
					</div>
				</div>

				<!-- Desktop Navigation - Left Arrow -->
				<button
					v-if="!loading && isLarge && canGoPrev"
					class="absolute left-[-12px] top-[131px] z-10 cursor-pointer transition-opacity hover:opacity-80"
					@click="goPrev"
					aria-label="Previous brands"
				>
					<BaseIcon name="chevron_round" class="h-24px w-24px rotate-180" />
				</button>

				<!-- Swiper Container -->
				<div
					v-if="!loading && filteredBrands.length > 0"
					ref="swiperEl"
					class="swiper !-mx-[20px] overflow-hidden !px-[20px] mdp:!-mx-[32px] mdp:!px-[32px] lg:!mx-0 lg:!px-0"
				>
					<div class="swiper-wrapper">
						<div
							v-for="(brand, index) in filteredBrands"
							:key="index"
							class="swiper-slide !h-auto"
							:class="{ '!w-[264px]': !isLarge }"
							@click="handleBrandsCardClick"
						>
							<a
								:href="brand.link"
								target="_blank"
								rel="noopener noreferrer"
								class="flex h-full flex-col gap-12px rounded-sm bg-white pb-12px"
							>
								<!-- Brand Image -->
								<div
									class="h-[198px] w-full overflow-hidden rounded-sm bg-n-50 lg:h-[214.5px]"
								>
									<img
										:src="brand.image"
										:alt="brand.title"
										class="h-full w-full object-cover"
										loading="lazy"
									/>
								</div>

								<!-- Brand Title -->
								<div class="flex items-center justify-center px-12px">
									<p class="typography-s1-reg text-n-950">
										{{ brand.title }}
									</p>
								</div>
							</a>
						</div>
					</div>
				</div>

				<!-- Desktop Navigation - Right Arrow -->
				<button
					v-if="!loading && isLarge && canGoNext"
					class="absolute right-[-12px] top-[131px] z-10 cursor-pointer transition-opacity hover:opacity-80"
					@click="goNext"
					aria-label="Next brands"
				>
					<BaseIcon name="chevron_round" class="h-24px w-24px" />
				</button>
			</div>

			<!-- CTA Button -->
			<a :href="buttonLink" target="_blank" rel="noopener noreferrer">
				<BaseButton
					variant="secondary-orange"
					size="medium"
					class="w-full lg:w-auto"
					@click="handleButtonClick"
				>
					{{ buttonText }}
				</BaseButton>
			</a>
		</div>
	</section>
</template>
